import { type ReactNode } from 'react';
import { Trophy } from 'lucide-react';
import { useLeaderboard } from '../hooks/use-leaderboard';

const AMOUNTS = [150, 100, 50];

function formatWeekRange(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? 6 : day - 1;
  const mon = new Date(now);
  mon.setDate(now.getDate() - diff);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmt = (d: Date): string =>
    d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} – ${fmt(sun)}`;
}

function nextPaymentDate(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString(
    'es-ES',
    { day: 'numeric', month: 'long' }
  );
}

type PositionInfo = { position: number; creatorsToNext: number | null };

function useWeeklyPosition(userId: string | undefined): PositionInfo {
  const { data } = useLeaderboard('week');
  if (!data || !userId) return { position: 0, creatorsToNext: null };
  const sorted = [...data].sort((a, b) => b.creatorCount - a.creatorCount);
  const idx = sorted.findIndex((r) => r.id === userId);
  if (idx === -1) return { position: 0, creatorsToNext: null };
  const prev = sorted[idx - 1];
  const curr = sorted[idx];
  const gap =
    idx > 0 && prev && curr ? prev.creatorCount - curr.creatorCount + 1 : null;
  return { position: idx + 1, creatorsToNext: gap };
}

function StatusText({ position, creatorsToNext }: PositionInfo): ReactNode {
  const hasBonus = position > 0 && position <= AMOUNTS.length;
  if (hasBonus) {
    return (
      <>
        <p className="text-lg font-bold text-success">
          ${AMOUNTS[position - 1]}
        </p>
        <p className="text-[10px] text-text-tertiary">
          Provisional — puede cambiar antes del cierre
        </p>
      </>
    );
  }
  return (
    <p className="text-xs text-text-secondary">
      {creatorsToNext !== null
        ? `Te faltan ${creatorsToNext} creadores para entrar al top 3`
        : 'Sin posición en el ranking esta semana'}
    </p>
  );
}

export function BonusProgress({ userId }: { userId: string }): ReactNode {
  const info = useWeeklyPosition(userId);
  return (
    <div className="rounded-xl bg-white/[0.02] p-base">
      <div className="flex items-center gap-sm">
        <Trophy size={14} className="text-warning" />
        <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
          Bonus semanal — {formatWeekRange()}
        </p>
      </div>
      <div className="mt-sm">
        {info.position > 0 && (
          <p className="text-xs text-text-secondary">Puesto #{info.position}</p>
        )}
        <StatusText {...info} />
      </div>
      <p className="mt-sm text-[10px] text-text-tertiary">
        Se incluirá en el pago del {nextPaymentDate()}
      </p>
    </div>
  );
}
